import * as React from "react";

import {
  getDinkusForProduct,
  getHeadingForProduct,
  getImagesForProduct,
  getUrlForProduct,
  PresentationProduct,
  PresentProductColorInfo,
} from "../model";

import {
  usePlpPresenterForTheFilterMenu_Desktop,
  useProductListController_forProduct,
  usePlpPresenterForTheProductsMasonry,
  usePlpAnalytics,
} from "../providers";

import { ProductTile } from "../product-tile";

interface ProductProps {
  product?: PresentationProduct;
  index: number;
}

const calculateSlidesToShow = (
  shouldShowTheFilterMenu_onDesktop: boolean,
  numberOfColumns: number,
  colors: PresentProductColorInfo[]
): [number, number, boolean] => {
  // why not use window.innerWidth? well it turns out that value doesn't update correctly and consistently for all resize events - documentElement.clientWidth does.
  const windowWidth = window.document.documentElement.clientWidth;

  const isMobile = windowWidth < 1024;
  const isDesktop = !isMobile;

  const swatchWidth = isDesktop ? 31 : 38;
  const margin = 8;
  const sizeOfArrow = 24;

  const maxDesktopWidth = 1170;
  let allowedProductWidth = windowWidth;
  if (isMobile) {
    // mobile has padding on the sides of the columns
    allowedProductWidth = allowedProductWidth - 40;
  } else {
    allowedProductWidth =
      allowedProductWidth > maxDesktopWidth
        ? maxDesktopWidth
        : allowedProductWidth;

    if (windowWidth < maxDesktopWidth + 40) {
      allowedProductWidth = allowedProductWidth - 40;
    }
  }

  const columnGap = 20;
  const filterColumnGap = 30;
  if (isDesktop && shouldShowTheFilterMenu_onDesktop) {
    allowedProductWidth = allowedProductWidth - filterColumnGap;
    allowedProductWidth =
      allowedProductWidth - (numberOfColumns - 1) * columnGap;
    allowedProductWidth = allowedProductWidth / (numberOfColumns + 1);
  } else {
    allowedProductWidth =
      allowedProductWidth - (numberOfColumns - 1) * columnGap;
    allowedProductWidth = allowedProductWidth / numberOfColumns;
  }

  // figure out the max swatches we can display in the allowed product width
  let maxSwatchesToDisplay: number;
  let numberOfItems = 0;
  let currentWidth = 0;
  while (1) {
    const candidateWidth = swatchWidth + margin;

    if (currentWidth + candidateWidth < allowedProductWidth) {
      numberOfItems += 1;
      currentWidth += candidateWidth;
    } else {
      break;
    }
  }
  maxSwatchesToDisplay = numberOfItems;
  if (isDesktop && maxSwatchesToDisplay > 5) {
    maxSwatchesToDisplay = 5;
  }

  // figure out how many swatches to display for the colors provided
  let numberOfSwatchesToDisplay = 0;
  currentWidth = 0;
  let partial = false;
  colors.forEach((color) => {
    const candidateWidth = swatchWidth + margin;

    if (numberOfSwatchesToDisplay < maxSwatchesToDisplay) {
      numberOfSwatchesToDisplay += 1;
      currentWidth += candidateWidth;
    } else {
      partial = true;
    }
  });

  // figure out whether we need to show a half swatch at the end of the list on mobile
  let isMinimalWidth = numberOfSwatchesToDisplay < maxSwatchesToDisplay;
  if (!isDesktop && !isMinimalWidth && partial) {
    const maxAllowedWidthWithHalfASwatchShowing =
      allowedProductWidth - swatchWidth / 2 - margin;
    if (currentWidth > maxAllowedWidthWithHalfASwatchShowing) {
      currentWidth = currentWidth - swatchWidth - margin;
      numberOfSwatchesToDisplay = numberOfSwatchesToDisplay - 1;
    }
    numberOfSwatchesToDisplay += 0.5;
    currentWidth += margin + swatchWidth / 2;
  }

  // should we show arrows on the desktop color swatch?
  // if so, then recalculate the number of swatches taking into account there is less width now because of the arrows
  let showArrows = false;
  if (isDesktop && colors.length > numberOfSwatchesToDisplay) {
    showArrows = true;
    allowedProductWidth = allowedProductWidth - sizeOfArrow * 2;

    maxSwatchesToDisplay = 0;
    numberOfItems = 0;
    currentWidth = 0;
    while (1) {
      const candidateWidth = swatchWidth + margin;

      if (currentWidth + candidateWidth < allowedProductWidth) {
        numberOfItems += 1;
        currentWidth += candidateWidth;
      } else {
        break;
      }
    }
    maxSwatchesToDisplay = numberOfItems;
    if (isDesktop && maxSwatchesToDisplay > 5) {
      maxSwatchesToDisplay = 5;
    }

    numberOfSwatchesToDisplay = 0;
    currentWidth = sizeOfArrow * 2;
    colors.forEach((color) => {
      const candidateWidth = swatchWidth + margin;

      if (numberOfSwatchesToDisplay < maxSwatchesToDisplay) {
        numberOfSwatchesToDisplay += 1;
        currentWidth += candidateWidth;
      }
    });
  }

  return [numberOfSwatchesToDisplay, currentWidth, showArrows];
};

export const Product: React.FunctionComponent<ProductProps> = (props) => {
  const { product } = props;

  const { analytics } = usePlpAnalytics();

  const { handleActionProduct } = useProductListController_forProduct();

  const { shouldShowTheFilterMenu_onDesktop } =
    usePlpPresenterForTheFilterMenu_Desktop();

  const { numberOfColumnsToDisplay } = usePlpPresenterForTheProductsMasonry();

  const [windowWidth, setWindowWidth] = React.useState(
    window.document.documentElement.clientWidth
  );

  const {
    presentationColors: colors,
    presentationAutomaticallySelectedColorKey: automaticallySelectedColorKey,
  } = product || { presentationColors: [] };

  const [slidesToShow, slidesContainerWidth, showArrowsOnColors] =
    React.useMemo(
      () =>
        calculateSlidesToShow(
          shouldShowTheFilterMenu_onDesktop,
          numberOfColumnsToDisplay,
          colors
        ),
      [
        shouldShowTheFilterMenu_onDesktop,
        numberOfColumnsToDisplay,
        colors,
        windowWidth,
      ]
    );

  const [selectedColor, setSelectedColor] = React.useState(
    automaticallySelectedColorKey
  );

  const heading = React.useMemo(
    () => getHeadingForProduct(product, selectedColor),
    [product, selectedColor]
  );

  const dinkus = React.useMemo(
    () => getDinkusForProduct(product, selectedColor),
    [product, selectedColor]
  );

  const images = React.useMemo(
    () => getImagesForProduct(product, selectedColor, !!(automaticallySelectedColorKey && selectedColor && selectedColor === automaticallySelectedColorKey)),
    [product, selectedColor]
  );

  const url = React.useMemo(
    () => getUrlForProduct(product, selectedColor),
    [product, selectedColor]
  );

  const priceAfter = React.useMemo(() => {
    const { special_price } = product || {};

    let priceAfter;
    if (special_price) {
      const formattedPrice = parseFloat(special_price as any);
      if (!isNaN(formattedPrice)) {
        priceAfter = formattedPrice.toFixed(2);
      }
    }

    return priceAfter;
  }, [product]);

  const priceBefore = React.useMemo(() => {
    const { price } = product || {};

    let priceBefore = Number(0).toFixed(2);
    if (price) {
      const formattedPrice = parseFloat(price as any);
      if (!isNaN(formattedPrice)) {
        priceBefore = formattedPrice.toFixed(2);
      }
    }

    return priceBefore;
  }, [product]);

  const rewardsPrice = React.useMemo(() => {
    const { rewards_price } = product || {};

    let rewardsPrice;
    if (rewards_price) {
      const formattedPrice = parseFloat(rewards_price as any);
      if (!isNaN(formattedPrice)) {
        rewardsPrice = formattedPrice.toFixed(2);
      }
    }

    return rewardsPrice;
  }, [product]);

  React.useEffect(() => {
    const onResize = () => {
      const newWindowWidth = window.document.documentElement.clientWidth;
      setWindowWidth(newWindowWidth);
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const handleActionColorSwatch = (color: PresentProductColorInfo) => {
    setSelectedColor(color.key);

    if (!props.product) {
      return;
    }

    const fredhopper_magento_id_for_the_whole_item =
      props.product.magento_id_product;
    const color_swatch_selected_value = color.value;
    const corresponding_grouped_product_name_for_color_swatch =
      getHeadingForProduct(props.product, color.key);
    analytics.clickProductColorSwatch({
      fredhopper_magento_id_for_the_whole_item,
      color_swatch_selected_value,
      corresponding_grouped_product_name_for_color_swatch,
    });
  };

  const handleAction = () => {
    if (props.product) {
      handleActionProduct(props.product, props.index);
    }
  };

  return (
    <ProductTile
      url={url}
      handleAction={handleAction}
      images={images}
      heading={heading}
      priceLabel={product?.priceLabel}
      priceAfter={priceAfter}
      priceBefore={priceBefore}
      rewardsPrice={rewardsPrice}
      colors={colors}
      colorSlidesContainerWidth={slidesContainerWidth}
      colorSlidesToShow={slidesToShow}
      showArrowsOnColors={showArrowsOnColors}
      selectedColor={selectedColor}
      handleActionColorSwatch={handleActionColorSwatch}
      promoNotes={product?.promoNotes}
      stockStatus={product?.stockStatus}
      dinkus={dinkus}
    />
  );
};
